import { useCallback } from "react";
import { Track } from "livekit-client";
import { TrackReference } from "@livekit/components-react";
import { isEqualTrackRef } from "@livekit/components-core";
import { StudioTemplate } from "../features/api/apiSlice";

export const useLayoutStyles = (
  videoTracks: TrackReference[],
  activeTemplate?: StudioTemplate
) => {
  const getVideoPositionStyle = useCallback(
    (track: TrackReference): React.CSSProperties => {
      if (!activeTemplate?.elements?.length) {
        return getDefaultPositionStyle(track);
      }

      const position = activeTemplate.elements.find((element: any) => {
        if (element.type === "camera" && track.source === Track.Source.Camera) {
          return true;
        }
        if (element.type === "screen" && track.source === Track.Source.ScreenShare) {
          return true;
        }
        if (
          typeof element.index === "number" &&
          videoTracks.findIndex((t) => isEqualTrackRef(t, track)) === element.index
        ) {
          return true;
        }
        return false;
      });

      if (position) {
        return {
          position: "absolute",
          top: typeof position.top === "number" ? `${position.top}%` : position.top,
          left: typeof position.left === "number" ? `${position.left}%` : position.left,
          width: typeof position.width === "number" ? `${position.width}%` : position.width,
          height: typeof position.height === "number" ? `${position.height}%` : position.height,
          zIndex: position.z_index || "auto",
          display: position.display || "flex"
        };
      }

      return getDefaultPositionStyle(track);
    },
    [activeTemplate, videoTracks]
  );

  const getDefaultPositionStyle = useCallback((track: TrackReference): React.CSSProperties => {
    const index = videoTracks.findIndex((t) => isEqualTrackRef(t, track));
    if (index < 0) return {};

    const totalTracks = videoTracks.length;
    const columns = Math.ceil(Math.sqrt(totalTracks));
    const rows = Math.ceil(totalTracks / columns);
    const col = index % columns;
    const row = Math.floor(index / columns);
    const width = 100 / columns;
    const height = 100 / rows;

    return {
      position: "absolute",
      top: `${row * height}%`,
      left: `${col * width}%`,
      width: `${width}%`,
      height: `${height}%`,
    };
  }, [videoTracks]);

  return {
    getVideoPositionStyle
  };
};